import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAgency } from '../lib/AgencyContext';
import { useI18n } from '../lib/i18n';
import { digitsOnly, pibError } from '../lib/validation';
import Icon from './Icon';
import { suggestAccounts, cleanCity } from '../lib/banks';

// ============================================================
// Automatsko preuzimanje podataka firme po PIB-u (domaći klijenti).
// Čim je upisan ispravan PIB (9 cifara + kontrolna cifra), pretraga kreće sama
// (Supabase funkcija "nbs-company-lookup"). onFound({ name, mb, address, city, postal_code }).
// ============================================================
// own: podaci sopstvene firme (pravljenje naloga), inače klijent
export default function CompanyLookup({ pib, onFound, own = false }) {
  const { tr } = useI18n();
  const { isAdmin } = useAgency();
  const [state, setState] = useState({ busy: false, msg: '', err: '', detail: '', accounts: [] });
  const lastSearched = useRef('');

  const clean = digitsOnly(pib);
  const ready = clean.length === 9 && !pibError(clean);

  const search = async (value) => {
    lastSearched.current = value;
    setState({ busy: true, msg: '', err: '', detail: '', accounts: [] });
    const { data, error } = await supabase.functions.invoke('nbs-company-lookup', { body: { pib: value } });
    let problem = '';
    if (error) {
      problem = error.message;
      try { const b = await error.context?.json(); if (b?.error) problem = b.error; } catch { /* ignore */ }
    } else if (data?.error) problem = data.error;
    if (problem) {
      setState({ busy: false, msg: '', accounts: [], detail: problem,
        err: tr('Podaci trenutno nisu mogli da se preuzmu. Pokušajte ponovo ili ih unesite ručno.', 'The details could not be fetched right now. Try again or enter them manually.', 'Данные сейчас не удалось получить. Попробуйте снова или введите вручную.') });
      return;
    }
    if (!data?.found) {
      // Administrator vidi početak odgovora registra (za doradu)
      setState({ busy: false, msg: '', accounts: [], detail: data?.raw ? String(data.raw).slice(0, 700) : '',
        err: tr('Za ovaj PIB nema podataka. Proverite broj ili unesite podatke ručno.', 'No data for this tax ID. Check the number or enter the details manually.', 'По этому ИНН данных нет.') });
      return;
    }
    // Samo naziv mesta, bez broja pošte i opštine ("BEOGRAD (STARI GRAD)" → "BEOGRAD")
    const c ={ ...(data.company || {}) };
    if (c.city) c.city = cleanCity(c.city);
    // Računi za podatke sopstvene firme: prvi aktivni dinarski i devizni (ako ga registar vrati)
    const accs = data.accounts || [];
    const { dom, fx } = suggestAccounts(accs);
    onFound(c, accs, dom?.account || '', fx?.account || '');
    setState({ busy: false, err: '', detail: '', accounts: [],
      msg: tr('Podaci firme su popunjeni. Proverite ih pre čuvanja.', 'Company details filled in. Please check them before saving.', 'Данные заполнены. Проверьте перед сохранением.') });
  };

  // Pretraga kreće sama kad PIB postane ispravan (jednom po PIB-u)
  useEffect(() => {
    if (ready && clean !== lastSearched.current) search(clean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clean, ready]);

  return (
    <div style={s.box}>
      <div style={s.note}>
        <Icon name="info" size={15} color="var(--lavender-700)" />
        <span>{own
          ? tr('Upišite PIB i podaci vaše firme se preuzimaju automatski.',
            'Enter the tax ID (PIB) and your company details are filled in automatically.',
            'Введите ИНН (PIB), и данные вашей фирмы заполнятся автоматически.')
          : tr('Ukoliko je vaš klijent registrovan u Srbiji, automatski preuzimamo podatke putem PIB-a.',
            'If your client is registered in Serbia, we fetch the details automatically using the tax ID (PIB).',
            'Если клиент зарегистрирован в Сербии, данные подтягиваются автоматически по ИНН (PIB).')}</span>
      </div>
      {state.busy && <div style={s.status}>{tr('Preuzimam podatke…', 'Fetching details…', 'Загрузка данных…')}</div>}
      {state.err && (
        <div style={s.err}>
          {state.err}
          {ready && <button type="button" onClick={() => search(clean)} style={s.retry}>{tr('Pokušaj ponovo', 'Try again', 'Повторить')}</button>}
          {isAdmin && state.detail && <div style={s.detail}>{state.detail}</div>}
        </div>
      )}
    </div>
  );
}

const s = {
  box: { display: 'flex', flexDirection: 'column', gap: 6, margin: '-4px 0 14px' },
  note: { display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, lineHeight: 1.45, color: 'var(--ink-700)', background: 'var(--lavender-100)', borderRadius: 8, padding: '8px 10px' },
  status: { fontSize: 12.5, color: 'var(--ink-500)' },
  err: { fontSize: 12.5, color: 'var(--red-600)', background: 'var(--red-100)', borderRadius: 8, padding: '8px 10px' },
  retry: { marginLeft: 8, border: 'none', background: 'none', padding: 0, color: 'var(--teal-800)', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline' },
  detail: { marginTop: 4, fontSize: 11.5, color: 'var(--ink-500)', wordBreak: 'break-word' },
};
